/**
 * PillarProgressionPopup — Iter. 406.k
 *
 * Dopaminergic Dashboard popup that fires when a universal pillar gains
 * >=1.5pt week-over-week. Mounts silently on Dashboard, fetches
 * /api/insights/pillar-progression once per session, and renders a glowing
 * card celebrating the pillar with the biggest weekly jump.
 *
 * Behaviour
 * ─────────
 *   • Auto-dismiss after 6s (timer pauses on hover/focus).
 *   • Backend marks the popup "seen" for the current ISO week so it never
 *     re-shows after dismissal.
 *   • Idempotent on remount — uses a sessionStorage flag scoped per user
 *     to avoid double-render in React Strict Mode.
 *   • iOS-safe via `useCameraViewport` shell so the body never scrolls
 *     under the toast.
 *
 * Re-usable: drop in any tab where users land after activity — Dashboard
 * (default), WeeklyRecap, MonthlyReport, etc.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, TrendingUp, X, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../context/AppContext';

const SESSION_FLAG_PREFIX = 'yumix_pillar_popup_shown_';

/**
 * The popup only mounts the API call on the first Dashboard visit per session.
 * Dismissal is persisted server-side (db.pillar_popups_seen) so a refresh
 * tomorrow won't reshow it for the same ISO week.
 */
export default function PillarProgressionPopup({ userId, language = 'fr' }) {
  const fr = language === 'fr';
  const navigate = useNavigate();
  const [celebration, setCelebration] = useState(null);
  const [closing, setClosing] = useState(false);
  const timerRef = useRef(null);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (!userId || hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    try {
      const sessionKey = `${SESSION_FLAG_PREFIX}${userId}`;
      if (sessionStorage.getItem(sessionKey)) return;
    } catch { /* ignore */ }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/insights/pillar-progression');
        if (cancelled) return;
        const data = res?.data || {};

        // Iter. 406.l — Monthly "Bilan du mois" takes priority over the
        // weekly popup if the user is in days 1-7 of a new month and gained
        // >=3pt on any pillar vs last month.
        const monthly = data.monthly_celebration;
        if (monthly && !data.monthly_popup_seen) {
          setCelebration({
            ...monthly,
            kind: 'monthly',
            current: null,
            previous: null,
          });
          try {
            sessionStorage.setItem(`${SESSION_FLAG_PREFIX}${userId}`, '1');
          } catch { /* ignore */ }
          return;
        }

        if (data.popup_seen) return; // weekly already dismissed for this ISO week
        if (data.top_celebration && data.top_celebration.celebrate) {
          setCelebration({ ...data.top_celebration, kind: 'weekly' });
          try {
            sessionStorage.setItem(`${SESSION_FLAG_PREFIX}${userId}`, '1');
          } catch { /* ignore */ }
        }
      } catch (err) {
        // Endpoint is best-effort. Never break the Dashboard if it fails.
        // (No new collection seeded ⇒ delta=null ⇒ no popup. That's fine.)
      }
    })();

    return () => { cancelled = true; };
  }, [userId]);

  const handleClose = useCallback(async () => {
    if (closing) return;
    setClosing(true);
    try {
      if (celebration?.kind === 'monthly') {
        await api.post('/insights/pillar-progression/seen-monthly', {
          pillar_id: celebration?.id,
          year_month: celebration?.year_month,
        });
      } else {
        await api.post('/insights/pillar-progression/seen', {
          pillar_id: celebration?.id,
        });
      }
    } catch { /* ignore — popup will simply re-trigger if the page is reloaded */ }
    setTimeout(() => setCelebration(null), 350);
  }, [closing, celebration]);

  // Auto-dismiss 6s after the celebration first appears.
  useEffect(() => {
    if (!celebration || closing) return undefined;
    timerRef.current = setTimeout(() => handleClose(), 6000);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [celebration, closing, handleClose]);

  const handleCTA = () => {
    handleClose();
    navigate('/monthly-report');
  };

  if (!celebration) return null;

  const isMonthly = celebration.kind === 'monthly';
  const pillarName = fr ? celebration.name_fr : celebration.name_en;
  const deltaStr = `+${celebration.delta.toFixed(1)}`;
  const monthLabel = isMonthly
    ? (() => {
        const m = celebration.month;
        const fullMonths = fr
          ? ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec']
          : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return m ? fullMonths[m - 1] : '';
      })()
    : null;

  return (
    <AnimatePresence>
      <motion.div
        key="pillar-progression-popup"
        initial={{ opacity: 0, y: -24, scale: 0.95 }}
        animate={closing ? { opacity: 0, y: -24, scale: 0.95 } : { opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 280, damping: 26 }}
        className="fixed left-1/2 -translate-x-1/2 z-[2147483600] w-[calc(100vw-2rem)] max-w-sm pointer-events-auto"
        style={{ top: 'max(1rem, env(safe-area-inset-top, 0px))' }}
        data-testid={isMonthly ? 'pillar-progression-popup-monthly' : 'pillar-progression-popup'}
        role="status"
        aria-live="polite"
      >
        <div
          className={
            isMonthly
              ? "relative rounded-2xl bg-gradient-to-br from-amber-500/35 via-rose-500/20 to-violet-500/30 border-2 border-amber-300/70 shadow-[0_0_60px_rgba(251,191,36,0.5)] backdrop-blur-xl overflow-hidden"
              : "relative rounded-2xl bg-gradient-to-br from-emerald-500/30 via-violet-500/20 to-amber-500/25 border-2 border-emerald-400/60 shadow-[0_0_50px_rgba(52,211,153,0.4)] backdrop-blur-xl overflow-hidden"
          }
          onMouseEnter={() => timerRef.current && clearTimeout(timerRef.current)}
          onMouseLeave={() => {
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => handleClose(), 4000);
          }}
        >
          {/* Ambient glows */}
          <div className={`pointer-events-none absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl ${isMonthly ? 'bg-amber-400/40' : 'bg-emerald-400/30'}`} />
          <div className={`pointer-events-none absolute -bottom-16 -left-16 w-40 h-40 rounded-full blur-3xl ${isMonthly ? 'bg-rose-500/30' : 'bg-violet-500/30'}`} />

          <button
            type="button"
            onClick={handleClose}
            aria-label={fr ? 'Fermer' : 'Close'}
            data-testid="pillar-progression-close"
            className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full bg-black/40 hover:bg-black/70 text-white flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>

          <div className="relative flex items-center gap-3 p-4 pr-10">
            {/* Icon */}
            <div
              className={`shrink-0 w-14 h-14 rounded-2xl border flex items-center justify-center text-3xl ${
                isMonthly
                  ? 'bg-amber-400/25 border-amber-300/60'
                  : 'bg-emerald-400/25 border-emerald-300/50'
              }`}
              aria-hidden="true"
              data-testid="pillar-progression-icon"
            >
              {celebration.icon || '🎯'}
            </div>

            {/* Body */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 mb-0.5">
                <Sparkles className={`w-3.5 h-3.5 shrink-0 ${isMonthly ? 'text-amber-200' : 'text-amber-300'}`} />
                <p className={`text-[10px] uppercase tracking-widest font-bold ${isMonthly ? 'text-amber-200' : 'text-amber-300'}`}>
                  {isMonthly
                    ? (fr ? `Bilan ${monthLabel} 🏆` : `${monthLabel} Recap 🏆`)
                    : (fr ? 'Progression hebdo' : 'Weekly progress')}
                </p>
              </div>
              <p className="font-heading uppercase text-sm leading-tight text-white truncate" data-testid="pillar-progression-name">
                {pillarName}
              </p>
              <div className="flex items-baseline gap-1.5 mt-1">
                <span
                  className={`text-2xl font-black leading-none ${isMonthly ? 'text-amber-200' : 'text-emerald-300'}`}
                  data-testid="pillar-progression-delta"
                >
                  {deltaStr}
                </span>
                <TrendingUp className={`w-4 h-4 shrink-0 ${isMonthly ? 'text-amber-200' : 'text-emerald-300'}`} />
                <span className="text-[10px] text-white/70 uppercase tracking-wider ml-1">
                  {isMonthly
                    ? (fr ? 'vs mois precedent' : 'vs prior month')
                    : (fr ? 'vs sem. derniere' : 'vs last week')}
                </span>
              </div>
              {!isMonthly && (
                <p className="text-[10px] text-white/60 mt-0.5">
                  {celebration.previous?.toFixed(1) ?? '0.0'}/10 → <strong className="text-white/90">{celebration.current?.toFixed(1) ?? '0.0'}/10</strong>
                </p>
              )}
              {isMonthly && (
                <p className="text-[10px] text-white/70 mt-0.5 leading-snug">
                  {fr
                    ? `Bravo, c'est le plus gros bond du mois sur ce pilier !`
                    : `Your biggest pillar leap of the month — well done!`}
                </p>
              )}
            </div>
          </div>

          {/* CTA strip */}
          <button
            type="button"
            onClick={handleCTA}
            data-testid="pillar-progression-cta"
            className={`w-full h-10 border-t border-white/10 text-[11px] uppercase tracking-widest font-bold text-white flex items-center justify-center gap-1.5 transition-colors ${
              isMonthly
                ? 'bg-gradient-to-r from-amber-500/35 to-rose-500/35 hover:from-amber-500/55 hover:to-rose-500/55'
                : 'bg-gradient-to-r from-emerald-500/30 to-violet-500/30 hover:from-emerald-500/50 hover:to-violet-500/50'
            }`}
          >
            {isMonthly
              ? (fr ? 'Voir le bilan complet' : 'See full recap')
              : (fr ? 'Voir mon rapport' : 'See my report')}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
